import './style.css';
import {
  calculateVoteResult,
  getUtcRoundId,
  normalizeCounts,
  percentage,
  type Choice,
  type VoteCounts,
} from './gameRules.ts';

export const debug_options = {
  showDebugPanel: false,
  logApi: false,
  logPlayfun: false,
  useMockApi: import.meta.env.VITE_USE_MOCK_API === 'true',
};

const GAME_SLUG = 'blue-or-red';
const VOTER_ID_KEY = `${GAME_SLUG}:voter-id`;
const INTRO_DISMISSED_KEY = `${GAME_SLUG}:intro-dismissed`;
const POINTS_ADDED_PREFIX = `${GAME_SLUG}:playfun-points-added:`;
const POINTS_SAVED_PREFIX = `${GAME_SLUG}:playfun-points-saved:`;
const PLAYFUN_GAME_ID = (import.meta.env.VITE_PLAYFUN_GAME_ID || '').trim();
const PLAYFUN_API_KEY = (
  import.meta.env.VITE_PLAYFUN_API_KEY || '64ee7b61-3b07-437b-bcea-565ee94a0ddc'
).trim();

type ApiRoundResponse =
  | { success: true; roundId: string; hasVoted: false }
  | {
      success: true;
      roundId: string;
      hasVoted: true;
      vote: Choice;
      counts: VoteCounts;
      blueWins: boolean;
      survives: boolean;
      points: number;
    }
  | { success: false; error?: string };

type ApiVoteResponse =
  | {
      success: true;
      roundId: string;
      hasVoted: true;
      vote: Choice;
      counts: VoteCounts;
      blueWins: boolean;
      survives: boolean;
      points: number;
      duplicate?: boolean;
    }
  | { success: false; error?: string };

interface OpenGameSdk {
  init(options: { gameId: string }): Promise<void> | void;
  addPoints(points: number): Promise<void> | void;
  endGame(): Promise<void>;
  on?(event: string, callback: (...args: unknown[]) => void): void;
}

declare global {
  interface Window {
    OpenGameSDK?: new (config: Record<string, unknown>) => OpenGameSdk;
  }
}

const elements = {
  redButton: document.querySelector<HTMLButtonElement>('#red-button'),
  blueButton: document.querySelector<HTMLButtonElement>('#blue-button'),
  redImage: document.querySelector<HTMLImageElement>('#red-image'),
  blueImage: document.querySelector<HTMLImageElement>('#blue-image'),
  introModal: document.querySelector<HTMLElement>('#intro-modal'),
  introDismiss: document.querySelector<HTMLButtonElement>('#intro-dismiss'),
  resultPanel: document.querySelector<HTMLElement>('#result-panel'),
  resultKicker: document.querySelector<HTMLElement>('#result-kicker'),
  resultTitle: document.querySelector<HTMLElement>('#result-title'),
  resultCopy: document.querySelector<HTMLElement>('#result-copy'),
  redPercent: document.querySelector<HTMLElement>('#red-percent'),
  bluePercent: document.querySelector<HTMLElement>('#blue-percent'),
  totalVotes: document.querySelector<HTMLElement>('#total-votes'),
  pointsEarned: document.querySelector<HTMLElement>('#points-earned'),
  meterRed: document.querySelector<HTMLElement>('#meter-red'),
  meterBlue: document.querySelector<HTMLElement>('#meter-blue'),
  debugPanel: document.querySelector<HTMLElement>('#debug-panel'),
  debugOutput: document.querySelector<HTMLElement>('#debug-output'),
};

const buttonAssets = {
  red: {
    up: '/buttons/redUP.jpg',
    down: '/buttons/redDOWN.jpg',
  },
  blue: {
    up: '/buttons/blueUP.jpg',
    down: '/buttons/blueDOWN.jpg',
  },
};

const sounds = {
  down: new Audio('/sounds/button-down.mp3'),
  up: new Audio('/sounds/button-up.mp3'),
};

const state = {
  roundId: getUtcRoundId(),
  voterId: getOrCreateVoterId(),
  locked: true,
  sdk: null as OpenGameSdk | null,
  sdkReady: false,
  pendingPlayfunAward: null as { roundId: string; points: number } | null,
};

function requireElement<T extends Element>(element: T | null, name: string): T {
  if (!element) throw new Error(`Missing required element: ${name}`);
  return element;
}

const redButton = requireElement(elements.redButton, 'red-button');
const blueButton = requireElement(elements.blueButton, 'blue-button');
const redImage = requireElement(elements.redImage, 'red-image');
const blueImage = requireElement(elements.blueImage, 'blue-image');

function trackPlay(): void {
  fetch('https://mann.cool/api/plays', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug: GAME_SLUG, source: 'direct' }),
  }).catch(() => {});
}

function getOrCreateVoterId(): string {
  const existing = localStorage.getItem(VOTER_ID_KEY);
  if (existing) return existing;

  const id = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  localStorage.setItem(VOTER_ID_KEY, id);
  return id;
}

function setText(element: HTMLElement | null, value: string): void {
  if (element) element.textContent = value;
}

function logDebug(label: string, data: unknown): void {
  if (!debug_options.showDebugPanel) return;
  if (elements.debugPanel) elements.debugPanel.hidden = false;
  if (elements.debugOutput) {
    elements.debugOutput.textContent = `${label}\n${JSON.stringify(data, null, 2)}`;
  }
}

function playSound(sound: HTMLAudioElement): void {
  sound.currentTime = 0;
  sound.play().catch(() => {});
}

function setPressed(choice: Choice, pressed: boolean): void {
  const button = choice === 'red' ? redButton : blueButton;
  const image = choice === 'red' ? redImage : blueImage;
  button.classList.toggle('is-pressed', pressed);
  image.src = pressed ? buttonAssets[choice].down : buttonAssets[choice].up;
}

function lockButtons(locked: boolean): void {
  state.locked = locked;
  redButton.disabled = locked;
  blueButton.disabled = locked;
  redButton.classList.toggle('is-locked', locked);
  blueButton.classList.toggle('is-locked', locked);
}

function clearWinnerState(): void {
  redButton.classList.remove('is-winner');
  blueButton.classList.remove('is-winner');
}

function initIntroModal(): void {
  const modal = elements.introModal;
  const dismiss = elements.introDismiss;
  if (!modal || !dismiss) return;

  const dismissed = localStorage.getItem(INTRO_DISMISSED_KEY) === '1';
  modal.hidden = dismissed;

  if (!dismissed) {
    requestAnimationFrame(() => dismiss.focus());
  }

  dismiss.addEventListener('click', () => {
    localStorage.setItem(INTRO_DISMISSED_KEY, '1');
    modal.hidden = true;
  });
}

async function fetchRound(): Promise<void> {
  lockButtons(true);

  try {
    const response = await fetch(`/api/round?voterId=${encodeURIComponent(state.voterId)}&roundId=${state.roundId}`);
    const data = (await response.json()) as ApiRoundResponse;

    if (debug_options.logApi) logDebug('GET /api/round', data);

    if (!data.success) throw new Error(data.error || 'Could not load round');

    if (data.hasVoted) {
      renderResult(data.vote, data.counts, data.points, false);
      return;
    }

    lockButtons(false);
    clearWinnerState();
  } catch (error) {
    if (debug_options.useMockApi) {
      lockButtons(false);
      return;
    }

    console.warn(error instanceof Error ? error.message : 'Could not load round');
  }
}

async function submitVote(choice: Choice): Promise<void> {
  if (state.locked) return;

  lockButtons(true);

  try {
    const data = debug_options.useMockApi
      ? mockVote(choice)
      : await postVote(choice);

    if (!data.success) throw new Error(data.error || 'Vote failed');

    renderResult(data.vote, data.counts, data.points, true);
    queuePlayfunAward(data.roundId, data.points);
  } catch (error) {
    lockButtons(false);
    console.warn(error instanceof Error ? error.message : 'Vote failed');
  }
}

async function postVote(choice: Choice): Promise<ApiVoteResponse> {
  const response = await fetch('/api/vote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      voterId: state.voterId,
      roundId: state.roundId,
      choice,
    }),
  });
  const data = (await response.json()) as ApiVoteResponse;
  if (debug_options.logApi) logDebug('POST /api/vote', data);
  return data;
}

function mockVote(choice: Choice): ApiVoteResponse {
  const red = choice === 'red' ? 51 : 49;
  const blue = choice === 'blue' ? 51 : 49;
  const counts = normalizeCounts({ red, blue });
  return {
    success: true,
    roundId: state.roundId,
    hasVoted: true,
    vote: choice,
    counts,
    ...calculateVoteResult(choice, counts),
  };
}

function renderResult(choice: Choice, countsInput: VoteCounts, points: number, freshVote: boolean): void {
  const counts = normalizeCounts(countsInput);
  const redPct = percentage(counts.red, counts.total);
  const bluePct = percentage(counts.blue, counts.total);

  elements.resultPanel?.classList.remove('survived', 'died');
  if (elements.resultPanel) elements.resultPanel.hidden = false;

  clearWinnerState();

  setText(elements.resultKicker, `${choice.toUpperCase()} VOTE LOCKED`);
  setText(elements.resultTitle, 'CURRENT TALLY');
  setText(elements.redPercent, `${redPct}%`);
  setText(elements.bluePercent, `${bluePct}%`);
  setText(elements.totalVotes, counts.total.toLocaleString());
  setText(elements.pointsEarned, points.toString());
  setText(elements.resultCopy, 'Final totals lock at midnight UTC. Check back tomorrow for payouts.');

  if (elements.meterRed) elements.meterRed.style.width = `${redPct}%`;
  if (elements.meterBlue) elements.meterBlue.style.width = `${bluePct}%`;

  redButton.disabled = false;
  blueButton.disabled = false;
  state.locked = true;

  if (!freshVote) return;
  setTimeout(() => playSound(sounds.up), 80);
}

function setupButtons(): void {
  for (const button of [redButton, blueButton]) {
    const choice = button.dataset.choice as Choice;

    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      setPressed(choice, true);
      playSound(sounds.down);
    });

    button.addEventListener('pointerup', (event) => {
      event.preventDefault();
      setPressed(choice, false);
      playSound(sounds.up);
      void submitVote(choice);
    });

    button.addEventListener('pointerleave', () => setPressed(choice, false));
    button.addEventListener('pointercancel', () => setPressed(choice, false));
    button.addEventListener('contextmenu', (event) => event.preventDefault());
  }
}

function initPlayfun(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="x-ogp-key"]');
  if (meta) meta.content = PLAYFUN_API_KEY;

  const configured = PLAYFUN_GAME_ID.length > 0 && PLAYFUN_API_KEY.length > 0 && !PLAYFUN_API_KEY.includes('%');

  if (!configured) {
    if (debug_options.logPlayfun) logDebug('Play.fun', 'Not configured');
    return;
  }

  const tryInit = (attempt = 0): void => {
    if (!window.OpenGameSDK) {
      if (attempt < 40) window.setTimeout(() => tryInit(attempt + 1), 250);
      return;
    }

    state.sdk = new window.OpenGameSDK({
      ui: { usePointsWidget: true, theme: 'dark' },
      logLevel: debug_options.logPlayfun ? 'info' : 'warn',
    });

    state.sdk.on?.('OnReady', () => {
      state.sdkReady = true;
      if (debug_options.logPlayfun) logDebug('Play.fun', 'ready');
      void flushPendingPlayfunAward();
    });

    state.sdk.on?.('GamePause', () => {
      document.body.classList.add('playfun-paused');
    });

    state.sdk.on?.('GameResume', () => {
      document.body.classList.remove('playfun-paused');
    });

    state.sdk.on?.('SavePointsSuccess', () => {
      if (state.pendingPlayfunAward) {
        localStorage.setItem(`${POINTS_SAVED_PREFIX}${state.pendingPlayfunAward.roundId}`, '1');
      }
    });

    try {
      void Promise.resolve(state.sdk.init({ gameId: PLAYFUN_GAME_ID })).catch(() => {});
    } catch {
      state.sdk = null;
      state.sdkReady = false;
    }
  };

  tryInit();
}

function queuePlayfunAward(roundId: string, points: number): void {
  if (points <= 0) return;
  if (localStorage.getItem(`${POINTS_SAVED_PREFIX}${roundId}`)) return;

  state.pendingPlayfunAward = { roundId, points };
  void flushPendingPlayfunAward();
}

async function flushPendingPlayfunAward(): Promise<void> {
  if (!state.pendingPlayfunAward || !state.sdk || !state.sdkReady) return;

  const { roundId, points } = state.pendingPlayfunAward;
  const pointsAddedKey = `${POINTS_ADDED_PREFIX}${roundId}`;

  try {
    if (!localStorage.getItem(pointsAddedKey)) {
      await Promise.resolve(state.sdk.addPoints(points));
      localStorage.setItem(pointsAddedKey, '1');
    }

    await state.sdk.endGame();
    localStorage.setItem(`${POINTS_SAVED_PREFIX}${roundId}`, '1');
    state.pendingPlayfunAward = null;
  } catch (error) {
    if (debug_options.logPlayfun) logDebug('Play.fun save failed', String(error));
  }
}

function boot(): void {
  trackPlay();
  initIntroModal();
  setupButtons();
  initPlayfun();
  void fetchRound();
}

boot();
